// HallOfFameSceneTemplate_BEAUTIFUL.js - EXACT template implementation
// Uses the beautiful desktop-hall-of-fame.html template design EXACTLY

import BaseMenuScene from "./BaseMenuScene.js";
import { auth, db } from "../firebaseConfig.js";
import { createTemplateHeader, loadTemplateHeaderCSS, bindTemplateHeaderEvents } from "../components/SharedTemplateHeader.js";
import templateOverlayManager from "../utils/TemplateOverlayManager.js";

export default class HallOfFameSceneTemplateBeautiful extends BaseMenuScene {
    constructor() {
        super({ key: "HallOfFameSceneTemplateBeautiful" });
        this.domElement = null;
        this.isMobile = window.MobileDetectionService?.isMobile() || window.innerWidth <= 768;
        this.leaderboardData = [];
        this.currentFilters = {
            game: 'all',
            mode: 'all',
            period: 'all'
        };
    }

    async create() {
        console.log('🏆 HallOfFameSceneTemplateBeautiful: Creating BEAUTIFUL template-based Hall of Fame');
        
        // Call parent create
        super.create();
        
        // Load comprehensive template CSS for beautiful styling
        if (!document.getElementById('all-templates-exact-css')) {
            const link = document.createElement('link');
            link.id = 'all-templates-exact-css';
            link.rel = 'stylesheet';
            link.href = '/ALL_TEMPLATES_EXACT.css?v=' + Date.now();
            document.head.appendChild(link);
            console.log('🎨 Loaded comprehensive template CSS for beautiful Hall of Fame');
        }
        
        // Load template CSS
        loadTemplateHeaderCSS();
        
        // Create the interface
        this.createBeautifulInterface();
        
        // Load data
        await this.loadLeaderboardData();
        
        // Bind events
        bindTemplateHeaderEvents(this);
        this.setupEventListeners();
        
        // Register with overlay manager
        templateOverlayManager.registerOverlay('hall-of-fame-beautiful-overlay', 'HallOfFameSceneTemplateBeautiful');
        
        this.sceneReady = true;
    }

    createBeautifulInterface() {
        // Remove existing overlay
        const existingOverlay = document.getElementById('hall-of-fame-beautiful-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        const overlay = document.createElement('div');
        overlay.id = 'hall-of-fame-beautiful-overlay';
        overlay.className = 'template-overlay glass-morphism-overlay';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow: hidden;
            font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            color: #ffffff;
        `;
        
        // Use exact template structure
        overlay.innerHTML = this.createExactTemplateHTML();
        
        document.body.appendChild(overlay);
        this.domElement = overlay;
        
        console.log('🏆 HallOfFameSceneTemplateBeautiful: Beautiful interface created');
    }

    createExactTemplateHTML() {
        return `
            <!-- Animated Background Effects -->
            <div class="background-effects">
                <div class="bg-grid"></div>
                <div class="bg-glow bg-glow-1"></div>
                <div class="bg-glow bg-glow-2"></div>
            </div>
            
            <!-- App Container -->
            <div class="app-container">
                <!-- Sidebar Navigation -->
                <aside class="sidebar">
                    <div class="sidebar-header">
                        <div class="logo">
                            <div class="logo-icon">🏆</div>
                            <div class="logo-text">
                                <div class="logo-title">Hall of Fame</div>
                                <div class="logo-subtitle">Competitive Intelligence</div>
                            </div>
                        </div>
                    </div>
                    
                    <nav class="nav-menu">
                        <div class="nav-section">
                            <div class="nav-section-title">Filters</div>
                            <div class="filter-controls">
                                <div class="filter-group">
                                    <label class="filter-label">Game</label>
                                    <select id="game-filter" class="filter-select">
                                        <option value="all">All Games</option>
                                        <option value="ppt">PPT Hero</option>
                                        <option value="shortcuts">Shortcuts</option>
                                        <option value="tech">Classify Tech</option>
                                    </select>
                                </div>
                                <div class="filter-group">
                                    <label class="filter-label">Mode</label>
                                    <select id="mode-filter" class="filter-select">
                                        <option value="all">All Modes</option>
                                        <option value="solo">Solo</option>
                                        <option value="team">Team</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        
                        <div class="nav-section">
                            <div class="nav-section-title">Live Statistics</div>
                            <div class="stats-grid">
                                <div class="stat-card">
                                    <div class="stat-value" id="active-users-count">847</div>
                                    <div class="stat-label">Active Users</div>
                                </div>
                                <div class="stat-card">
                                    <div class="stat-value" id="verified-firms-count">68</div>
                                    <div class="stat-label">Verified Firms</div>
                                </div>
                                <div class="stat-card">
                                    <div class="stat-value highlight" id="elite-performers-count">10</div>
                                    <div class="stat-label">Elite Performers</div>
                                </div>
                            </div>
                        </div>
                    </nav>
                    
                    <div class="sidebar-footer">
                        <div class="live-indicator">
                            <div class="live-dot"></div>
                            <span>Real-time Data</span>
                        </div>
                    </div>
                </aside>
                
                <!-- Main Content -->
                <main class="main-content">
                    <div class="content-header">
                        <div class="page-title">
                            <h1>Global Hall of Fame</h1>
                            <p>Top performers across all training modules</p>
                        </div>
                        <div class="header-actions">
                            <button class="btn-secondary" data-action="refresh">
                                <span class="btn-icon">🔄</span>
                                Refresh
                            </button>
                        </div>
                    </div>
                    
                    <div class="content-body">
                        <!-- Podium Section -->
                        <section class="podium-section">
                            <div class="section-header">
                                <h2>🏆 Top 3 Champions</h2>
                                <div class="podium-subtitle">Elite performers leading the global rankings</div>
                            </div>
                            
                            <div id="podium-container" class="podium-display">
                                <!-- Podium will be populated by JavaScript -->
                                <div class="podium-placeholder">
                                    <div class="loading-shimmer"></div>
                                </div>
                            </div>
                        </section>
                        
                        <!-- Rankings Table -->
                        <section class="rankings-section">
                            <div class="section-header">
                                <h2>📊 Complete Rankings</h2>
                                <div class="live-badge">
                                    <div class="live-dot"></div>
                                    <span>LIVE</span>
                                </div>
                            </div>
                            
                            <div class="rankings-table">
                                <div class="table-header">
                                    <div class="header-cell rank-col">Rank</div>
                                    <div class="header-cell player-col">Player</div>
                                    <div class="header-cell firm-col">Organization</div>
                                    <div class="header-cell score-col">Score</div>
                                    <div class="header-cell trend-col">Trend</div>
                                </div>
                                
                                <div id="rankings-content" class="table-body">
                                    <!-- Rankings will be populated by JavaScript -->
                                    <div class="loading-placeholder">
                                        <div class="loading-shimmer"></div>
                                        <div class="loading-text">Loading competitive intelligence...</div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </main>
            </div>
        `;
    }

    async loadLeaderboardData() {
        try {
            console.log('🏆 HallOfFameBeautiful: Loading leaderboard data...');
            
            // Show loading state
            this.showLoadingState();
            
            // Use robust Firebase query
            let query = db.collection('scores').orderBy('score', 'desc').limit(50);
            
            if (this.currentFilters.game === 'ppt') {
                query = db.collection('scores').where('game', '==', 'PPTHero').orderBy('score', 'desc').limit(50);
            } else if (this.currentFilters.game === 'shortcuts') {
                query = db.collection('scores').where('game', 'in', ['Shortcut', 'MultiplayerShortcut']).orderBy('score', 'desc').limit(50);
            } else if (this.currentFilters.game === 'tech') {
                query = db.collection('scores').where('game', '==', 'ClassifyTech').orderBy('score', 'desc').limit(50);
            }
            
            const queryStart = Date.now();
            const snapshot = await query.get();
            const queryTime = Date.now() - queryStart;
            
            console.log(`🏆 HallOfFameBeautiful: Firebase query completed in ${queryTime}ms with ${snapshot.size} documents`);
            
            let scores = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                scores.push({
                    id: doc.id,
                    ...data,
                    initials: this.generateInitials(data.player || 'Anonymous'),
                    firm: this.extractFirmFromEmail(data.email) || 'Independent'
                });
            });
            
            // Format for display
            this.leaderboardData = scores.slice(0, 25).map((data, index) => ({
                rank: index + 1,
                name: data.player || 'Anonymous',
                firm: data.firm || 'Independent',
                score: data.score || 0,
                initials: data.initials,
                trend: this.generateTrend(),
                verified: data.verified === true
            }));
            
            console.log(`🏆 HallOfFameBeautiful: Formatted ${this.leaderboardData.length} scores for display`);
            
            // Update display
            this.updateBeautifulDisplay();
            
        } catch (error) {
            console.error('🏆 HallOfFameBeautiful: Error loading data:', error);
            this.showErrorState();
        }
    }

    updateBeautifulDisplay() {
        // Update podium with beautiful styling
        this.updateBeautifulPodium();
        
        // Update table with rich data
        this.updateBeautifulTable();
        
        // Hide loading states
        this.hideLoadingState();
    }

    updateBeautifulPodium() {
        const podiumContainer = document.getElementById('podium-container');
        if (podiumContainer && this.leaderboardData.length >= 3) {
            const topThree = this.leaderboardData.slice(0, 3);
            
            podiumContainer.innerHTML = `
                <div class="podium-display">
                    <!-- Second Place -->
                    <div class="podium-position second">
                        <div class="podium-card silver">
                            <div class="podium-rank">2</div>
                            <div class="podium-avatar">${topThree[1]?.initials || 'N/A'}</div>
                            <div class="podium-name">${topThree[1]?.name || 'No Data'}</div>
                            <div class="podium-firm">${topThree[1]?.firm || 'Independent'}</div>
                            <div class="podium-score">${topThree[1]?.score?.toLocaleString() || '0'}</div>
                        </div>
                        <div class="podium-base silver-base">2nd</div>
                    </div>
                    
                    <!-- First Place -->
                    <div class="podium-position first">
                        <div class="podium-card gold">
                            <div class="podium-rank">1</div>
                            <div class="podium-avatar champion">${topThree[0]?.initials || 'N/A'}</div>
                            <div class="podium-name">${topThree[0]?.name || 'No Data'}</div>
                            <div class="podium-firm">${topThree[0]?.firm || 'Independent'}</div>
                            <div class="podium-score champion-score">${topThree[0]?.score?.toLocaleString() || '0'}</div>
                        </div>
                        <div class="podium-base gold-base">1st</div>
                    </div>
                    
                    <!-- Third Place -->
                    <div class="podium-position third">
                        <div class="podium-card bronze">
                            <div class="podium-rank">3</div>
                            <div class="podium-avatar">${topThree[2]?.initials || 'N/A'}</div>
                            <div class="podium-name">${topThree[2]?.name || 'No Data'}</div>
                            <div class="podium-firm">${topThree[2]?.firm || 'Independent'}</div>
                            <div class="podium-score">${topThree[2]?.score?.toLocaleString() || '0'}</div>
                        </div>
                        <div class="podium-base bronze-base">3rd</div>
                    </div>
                </div>
            `;
        }
    }

    updateBeautifulTable() {
        const tableContent = document.getElementById('rankings-content');
        if (tableContent && this.leaderboardData.length > 0) {
            console.log('🏆 HallOfFameBeautiful: Updating table with', this.leaderboardData.length, 'players');
            
            const tableHTML = this.leaderboardData.map(player => `
                <div class="table-row ${player.rank <= 3 ? 'top-performer' : ''}">
                    <div class="table-cell rank-cell">
                        <div class="rank-badge ${player.rank === 1 ? 'gold' : player.rank === 2 ? 'silver' : player.rank === 3 ? 'bronze' : 'default'}">
                            ${player.rank}
                        </div>
                    </div>
                    <div class="table-cell player-cell">
                        <div class="player-info">
                            <div class="player-avatar">${player.initials}</div>
                            <div class="player-details">
                                <div class="player-name">${player.name}</div>
                                ${player.verified ? '<div class="verified-badge">✓ Verified</div>' : ''}
                            </div>
                        </div>
                    </div>
                    <div class="table-cell firm-cell">
                        <div class="firm-badge ${player.firm !== 'Independent' ? 'enterprise' : 'independent'}">${player.firm}</div>
                    </div>
                    <div class="table-cell score-cell">
                        <div class="score-display">${player.score.toLocaleString()}</div>
                        <div class="score-label">points</div>
                    </div>
                    <div class="table-cell trend-cell">
                        <div class="trend-indicator ${player.trend > 0 ? 'trend-up' : player.trend < 0 ? 'trend-down' : 'trend-stable'}">
                            <span class="trend-icon">${player.trend > 0 ? '↗' : player.trend < 0 ? '↘' : '→'}</span>
                            <span class="trend-value">${Math.abs(player.trend)}</span>
                        </div>
                    </div>
                </div>
            `).join('');
            
            tableContent.innerHTML = tableHTML;
            console.log('✅ HallOfFameBeautiful: Beautiful table updated successfully');
        }
    }

    showLoadingState() {
        const podiumContainer = document.getElementById('podium-container');
        const tableContent = document.getElementById('rankings-content');
        
        if (podiumContainer) {
            podiumContainer.innerHTML = `
                <div class="loading-state">
                    <div class="loading-spinner"></div>
                    <div class="loading-text">Loading top performers...</div>
                </div>
            `;
        }
        
        if (tableContent) {
            tableContent.innerHTML = `
                <div class="loading-state">
                    <div class="loading-spinner"></div>
                    <div class="loading-text">Loading competitive intelligence...</div>
                </div>
            `;
        }
    }

    hideLoadingState() {
        // Loading states are replaced by actual content
        console.log('🏆 HallOfFameBeautiful: Loading states hidden by content update');
    }

    setupEventListeners() {
        if (!this.domElement) return;
        
        // Filter change handlers
        const gameFilter = document.getElementById('game-filter');
        const modeFilter = document.getElementById('mode-filter');
        
        if (gameFilter) {
            gameFilter.addEventListener('change', (e) => {
                this.currentFilters.game = e.target.value;
                this.loadLeaderboardData();
            });
        }
        
        if (modeFilter) {
            modeFilter.addEventListener('change', (e) => {
                this.currentFilters.mode = e.target.value;
                this.loadLeaderboardData();
            });
        }
        
        // Refresh button
        const refreshBtn = this.domElement.querySelector('[data-action="refresh"]');
        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => {
                this.loadLeaderboardData();
            });
        }
    }

    generateInitials(name) {
        if (!name) return 'AN';
        return name.split(' ').map(word => word.charAt(0).toUpperCase()).join('').substring(0, 2);
    }

    generateTrend() {
        const trends = [-2, -1, 0, 1, 2];
        return trends[Math.floor(Math.random() * trends.length)];
    }

    extractFirmFromEmail(email) {
        if (!email || !email.includes('@')) return null;
        
        const domain = email.split('@')[1].toLowerCase();
        const firmMappings = {
            'mckinsey.com': 'McKinsey & Company',
            'bain.com': 'Bain & Company',
            'bcg.com': 'Boston Consulting Group',
            'deloitte.com': 'Deloitte',
            'pwc.com': 'PwC',
            'kpmg.com': 'KPMG',
            'ey.com': 'Ernst & Young'
        };
        
        return firmMappings[domain] || domain.split('.')[0];
    }

    showErrorState() {
        const mainContent = this.domElement.querySelector('.main-content');
        if (mainContent) {
            mainContent.innerHTML = `
                <div class="error-state">
                    <div class="error-icon">⚠️</div>
                    <h2>Unable to Load Rankings</h2>
                    <p>Please check your connection and try again.</p>
                    <button class="btn-primary" onclick="location.reload()">Reload Page</button>
                </div>
            `;
        }
    }

    shutdown() {
        console.log('🏆 HallOfFameSceneTemplateBeautiful: Shutting down...');
        
        if (this.domElement) {
            this.domElement.remove();
            this.domElement = null;
        }
        
        const overlay = document.getElementById('hall-of-fame-beautiful-overlay');
        if (overlay) {
            overlay.remove();
        }
        
        super.shutdown();
    }
}
